import ServerErrorHandler from 'app/ErrorHandler';
import request from 'app/api/request';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { v4 as uuidv4 } from 'uuid';
import { i18n } from 'app/utils/i18n';

const queryKeys = {
  template: 'TEMPLATE',
  templates: 'TEMPLATES',
  selectedTemplates: 'TEMPLATES_SELECTED',
};

export function useTemplates({ organization, documentSuperId, type }) {
  return useQuery(
    [queryKeys.templates, organization, documentSuperId, type],
    async () => {
      const params = [
        documentSuperId ? 'documentSuperId=' + documentSuperId : '',
        type ? 'type=' + type : '',
        organization ? 'organization=' + organization : '',
      ]
        .filter((i) => i)
        .join('&');
      const res = await request.get(`/gaby/templates?${params}`);
      return res.data;
    },
    {
      onError: ServerErrorHandler,
    }
  );
}

export function useDocumentTemplates(documentSuperId) {
  return useQuery(
    [queryKeys.templates, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/templates/document/${documentSuperId}`);
      return res.data;
    },
    {
      enabled: !!documentSuperId,
      onError: ServerErrorHandler,
    }
  );
}

export function useTemplate(revisionId) {
  return useQuery(
    [queryKeys.template, revisionId],
    async () => {
      const res = await request.get(`/gaby/templates/${revisionId}`);
      return res.data;
    },
    {
      enabled: !!revisionId,
      onError: ServerErrorHandler,
    }
  );
}

export function useSaveTemplate() {
  const queryClient = useQueryClient();
  return useMutation(
    async (template) => {
      await request.post('/gaby/templates', template);
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.templates);
      },
    }
  );
}

export function useCopyTemplate() {
  const queryClient = useQueryClient();
  return useMutation(
    async (template) => {
      const newTemplate = {
        ...template,
        superId: uuidv4(),
        revisionId: uuidv4(),
        name: `${i18n('templates.copyof')} ${template.name}`,
        className: '',
      };
      return await request.post('/gaby/templates', newTemplate);
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.templates);
      },
    }
  );
}

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  return useMutation(
    async (template) => {
      await request.del(`/gaby/templates/${template.superId}`);
    },
    {
      onError: ServerErrorHandler,
      onSuccess: (_, template) => {
        queryClient.invalidateQueries(queryKeys.templates);
        queryClient.invalidateQueries(queryKeys.template, template.revisionId);
      },
    }
  );
}

export function useSelectedTemplates(documentSuperId) {
  return useQuery(
    [queryKeys.selectedTemplates, documentSuperId],
    async () => {
      const res = await request.get(`/gaby/templates/selected/${documentSuperId}`);
      return res.data;
    },
    {
      onError: ServerErrorHandler,
    }
  );
}

export function useUpdateSelectedTemplates(documentSuperId) {
  const queryClient = useQueryClient();

  return useMutation(
    async (templateIds) => {
      await request.put(`/gaby/templates/selected/${documentSuperId}`, templateIds);
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.selectedTemplates);
      },
    }
  );
}

export function useCompiledTemplate(superId, organization) {
  return useQuery(
    [queryKeys.template, superId, organization, 'compiled'],
    async () => {
      const res = await request.get(`/gaby/templates?superId=${superId}&organization=${organization}&compiled=true`);
      const template = res.data[0];
      template.structure.templateSuperId = template.superId;
      return template;
    },
    {
      enabled: !!superId && !!organization,
      keepPreviousData: true,
      onError: ServerErrorHandler,
    }
  );
}

export function useDocumentWithStyle(documentSuperId, templateId) {
  return useQuery(
    [queryKeys.templates, 'doc-with-style', documentSuperId, templateId],
    async () => {
      const {
        data: [doc],
      } = await request.get(`/gaby/documents?superId=${documentSuperId}&current=true&compileSass=true&useDraft=true&useTemplate=${templateId}`);
      const styleId = doc.content.design.find((s) => s.type === 'document').styleId;
      const { data: style } = await request.get(`/gaby/styles/${styleId}?documentRevisionId=${doc.revisionId}`);
      return { doc, style };
    },
    {
      enabled: !!documentSuperId && !!templateId,
      keepPreviousData: true,
      onError: ServerErrorHandler,
    }
  );
}

export function useSaveCompiledTemplate() {
  const queryClient = useQueryClient();
  return useMutation(
    async (template) => {
      const res = await request.post('/gaby/templates?compiled=true', template);
      return res.data;
    },
    {
      onError: ServerErrorHandler,
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.templates);
        queryClient.invalidateQueries(queryKeys.template);
      },
    }
  );
}

export function useApplyTemplateDraft(documentId) {
  const queryClient = useQueryClient();
  return useMutation(
    async ([documentRevisionId, templateId]) => {
      return await request.post(`/gaby/documents/${documentId}/actions?compileSass=true&useTemplate=${templateId}`, {
        action: 'apply_draft',
        revisionId: documentRevisionId,
      });
    },
    {
      enabled: !!documentId,
      onError: ServerErrorHandler,
      onSuccess: () => {
        queryClient.invalidateQueries('draft');
        queryClient.invalidateQueries(queryKeys.templates);
        queryClient.invalidateQueries(queryKeys.template);
      },
    }
  );
}
